<template>
  <Layout>
    <PageHeader :title="title" hasBackButton>
      <template #afterTitle>
        <div class="d-flex gap-2">
          <McButtonBack data-me="setting-vehicle-inspection-form-btn-back" />
          <McActionsDropdown
            v-if="isEdit && (hasAccess('delete') || hasAccess('create'))"
            :buttonProps="{ 'data-me': 'setting-vehicle-inspection-form-btn-other' }"
            :buttonText="$t('global.others')"
            :menuItems="actionItems"
          />
          <McButton
            v-if="hasAccess('update') || hasAccess('create')"
            data-me="setting-vehicle-inspection-form-btn-save"
            variant="primary"
            :text="$t('global.save')"
            icon="icon-outline_save"
            :disabled="isSaveDisabled"
            @click="handleFormSubmit"
          />
        </div>
      </template>
    </PageHeader>
    <FormulateForm
      ref="formRef"
      v-model="form"
      data-me="setting-vehicle-inspection-form"
      class="mc-form"
      @submit="handleSave"
    >
      <!-- Skeleton Loading -->
      <template v-if="isLoading">
        <!-- Informasi Template -->
        <h4 class="card-title">
          {{ $t('vehicleInspection.templateInformation') }}
        </h4>
        <b-card>
          <b-row>
            <!-- Nama Template -->
            <b-col sm="12" md="6" class="mb-3">
              <b-skeleton width="30%" height="14px" class="mb-2" />
              <b-skeleton height="38px" />
            </b-col>

            <!-- Deskripsi -->
            <b-col sm="12" md="6" class="mb-3">
              <b-skeleton width="20%" height="14px" class="mb-2" />
              <b-skeleton height="96px" />
            </b-col>

            <!-- Target Kendaraan -->
            <b-col sm="12" md="6">
              <b-skeleton width="25%" height="14px" class="mb-2" />
              <div class="d-flex flex-wrap gap-2 mb-2">
                <b-skeleton width="120px" height="20px" />
                <b-skeleton width="160px" height="20px" />
                <b-skeleton width="140px" height="20px" />
              </div>
              <b-skeleton height="38px" />
            </b-col>
          </b-row>
        </b-card>

        <!-- Daftar Pemeriksaan -->
        <h4 class="card-title">
          {{ $t('vehicleInspection.listInspection') }}
        </h4>
        <b-skeleton height="400px" />
      </template>
      <!-- End Skeleton Loading -->

      <!-- Informasi Template -->
      <h4 v-if="!isLoading" class="card-title">
        {{ $t('vehicleInspection.templateInformation') }}
      </h4>
      <b-card v-if="!isLoading">
        <b-row>
          <b-col sm="12" md="6">
            <FormulateInput
              v-model="form.name"
              data-me="setting-vehicle-inspection-form-input-name"
              name="name"
              maxlength="64"
              validation="required|max:64,length"
              :placeholder="$t('vehicleInspection.placeholder.templateName')"
              :label="$t('vehicleInspection.fields.templateName')"
              help="required"
              validation-behavior="blur"
              error-behavior="blur"
              :clearable="true"
              :validation-messages="{
                required: `${$t('global.validation.required')}`,
              }"
            />
          </b-col>
          <b-col sm="12" md="6">
            <FormulateInput
              v-model="form.description"
              data-me="setting-vehicle-inspection-form-input-description"
              type="textarea"
              name="description"
              :placeholder="$t('vehicleInspection.placeholder.description')"
              :label="$t('vehicleInspection.fields.description')"
              class="note-textarea"
              maxlength="256"
              rows="4"
              :clearable="true"
              :disabled="isLoading"
              validation="max:256,length"
            />
          </b-col>
          <b-col sm="12" md="6">
            <label class="formulate-input-label teal-300">
              {{ $t('setting.list.notification.form.targetVehicle') }}
            </label>
            <div class="d-flex flex-wrap gap-2 mb-2">
              <McRadioButton
                id="allVehicles"
                v-model="vehicleSelectionMode"
                data-me="setting-vehicle-inspection-form-radio-all-vehicles"
                name="vehicleSelectionMode"
                :label="$t('setting.list.notification.form.allVehicles')"
                initialValue="allVehicles"
                :fullWidth="false"
              />
              <McRadioButton
                id="vehicleGroup"
                v-model="vehicleSelectionMode"
                data-me="setting-vehicle-inspection-form-radio-vehicle-group"
                name="vehicleSelectionMode"
                :label="$t('setting.list.notification.form.selectedVgroup')"
                initialValue="vehicleGroup"
                :fullWidth="false"
              />
              <McRadioButton
                id="selectedVehicles"
                v-model="vehicleSelectionMode"
                data-me="setting-vehicle-inspection-form-radio-selected-vehicles"
                name="vehicleSelectionMode"
                :label="$t('setting.list.notification.form.selectedVehicles')"
                initialValue="selectedVehicles"
                :fullWidth="false"
              />
            </div>

            <FormulateInput
              v-if="vehicleSelectionMode === VEHICLE_SELECTION_MODES.vehicleGroup"
              id="vGroups"
              key="vehicle-groups"
              v-model="form.vgroupIds"
              data-me="setting-vehicle-inspection-form-input-vehicle-groups"
              name="vgroups"
              type="value-help-v2"
              :placeholder="`${$t('global.form.select')} ${$t('global.object.fleetGroup')}`"
              :modalTitle="`${$t('global.form.select')} ${$t('global.object.fleetGroup')}`"
              :listGetter="resourcefulQuery.vGroups"
              :tableFields="vGroupFields"
              validation="required"
              help="required"
              :validation-messages="{
                required: `${$t('global.object.fleetGroup')} ${$t('global.validation.required')}`,
              }"
              displayedBy="name"
              trackBy="id"
              :multiple="true"
              :fullObject="fullObject.vgroup"
              :visibleTagCount="3"
              validation-behavior="blur"
              error-behavior="blur"
              :hasDisabledItem="hasInaccessibleTarget"
              :disabledItem="$t('global.object.anotherGroup')"
              @update:fullObject="($event) => (fullObject.vgroup = $event)"
            />

            <FormulateInput
              v-if="vehicleSelectionMode === VEHICLE_SELECTION_MODES.selectedVehicles"
              id="vehicles"
              key="selected-vehicles"
              v-model="form.vehicleIds"
              data-me="setting-vehicle-inspection-form-input-vehicles"
              name="vehicles"
              type="report-filter-value-help"
              :multiple="true"
              :placeholder="`${$t('global.form.select')} ${$t('global.object.licensePlate')}`"
              :modalTitle="`${$t('global.form.select')} ${$t('master.kendaraan')}`"
              :listGetter="resourcefulQuery.vehicles"
              :tableFields="vehicleFields"
              displayedBy="licensePlate"
              trackBy="id"
              :fullObject="fullObject.licensePlate"
              validation="required"
              help="required"
              :validation-messages="{
                required: `${$t('global.object.licensePlate')} ${$t('global.validation.required')}`,
              }"
              :visibleTagCount="3"
              validation-behavior="blur"
              error-behavior="blur"
              :hasDisabledItem="hasInaccessibleTarget"
              :disabledItem="$t('global.object.anotherGroup')"
              @update:fullObject="($event) => (fullObject.licensePlate = $event)"
            />
          </b-col>
        </b-row>
      </b-card>

      <!-- Daftar Pemeriksaan -->
      <h4 v-if="!isLoading" class="card-title">
        {{ $t('vehicleInspection.listInspection') }}
      </h4>
      <InspectionCategoryBuilder v-if="!isLoading" :key="$route.fullPath" v-model="form.sections" />
    </FormulateForm>

    <b-modal ref="modal-confirm-delete" :title="$t('global.delete')">
      <div class="card-body">
        {{ $t('global.confirmDelete1') }} {{ $t('global.confirmDelete2') }}
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            data-me="setting-vehicle-inspection-modal-delete-btn-cancel"
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton
            data-me="setting-vehicle-inspection-modal-delete-btn-confirm"
            variant="primary"
            :text="$t('global.delete')"
            :disabled="isDeleting"
            @click="handleDelete"
          />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import i18n from '@/i18n'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McRadioButton from '@/components/McRadioButton'
import McActionsDropdown from '@/components/McActionsDropdown'
import InspectionCategoryBuilder from './components/InspectionCategoryBuilder.vue'
import { VEHICLE_SELECTION_MODES } from '../lib/constants'
import {
  buildSectionsPayload,
  getDuplicateName,
  hasInvalidInspectionSections,
  mapSectionsToForm,
  hasAccess,
} from '../lib/utils'

import { GET_VGROUPS_PAGINATED } from '@/services/api/vehicle/vgroup'
import { GET_VEHICLE_FILTER } from '@/services/api/master/fleet'
import {
  CREATE_VEHICLE_INSPECTION_TEMPLATE,
  DELETE_VEHICLE_INSPECTION_TEMPLATE,
  GET_VEHICLE_INSPECTION_TEMPLATE,
  UPDATE_VEHICLE_INSPECTION_TEMPLATE,
} from '@/services/api/setting/vehicleInspection'

export default {
  page: {
    title: () => i18n.t('global.setting'),
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McRadioButton,
    McActionsDropdown,
    InspectionCategoryBuilder,
  },
  data() {
    return {
      form: {
        description: '',
        name: '',
        vgroupIds: [],
        vehicleIds: [],
        sections: [],
      },
      fullObject: {
        vgroup: [],
        licensePlate: [],
      },
      isLoading: !!(this.$route.params.templateId || this.$route.query.duplicateFrom),
      VEHICLE_SELECTION_MODES,
      vehicleSelectionMode: VEHICLE_SELECTION_MODES.allVehicles,
      isDeleting: false,
      isSaving: false,
      hasInaccessibleTarget: false,
    }
  },
  computed: {
    vGroupFields() {
      return [
        {
          key: 'name',
          label: this.$t('global.object.fleetGroup'),
        },
        {
          key: 'totalVehicle',
          label: this.$t('global.object.totalFleet'),
        },
      ]
    },
    vehicleFields() {
      return [
        {
          label: this.$t('report.fields.licensePlate'),
          key: 'licensePlate',
          isSortable: true,
        },
        {
          label: this.$t('report.fields.vehicleType'),
          key: 'vehicleType',
          isSortable: false,
          formatter: (value) => {
            return value
              ? [value.manufacturer ?? '-', value.model ?? '-', value.type ?? '-'].join(' / ')
              : '-'
          },
        },
        {
          label: this.$t('report.fields.vehicleGroup'),
          key: 'vehicle2Vgroup',
          isSortable: false,
          formatter: (value) => {
            return value && value.length > 0 ? value.map((v) => v.vgroup.name).join(', ') : '-'
          },
        },
      ]
    },
    resourcefulQuery() {
      return {
        vGroups: async (opts) => {
          let result = {}

          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            query: GET_VGROUPS_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
              },
            },
          })

          let vgroups = res.data.vgroupsPaginated.vgroups

          result = {
            metadata: {
              total_count: vgroups.pagination.total,
              page: vgroups.pagination.page,
            },
            data: {
              paginated_result: vgroups.data,
            },
          }

          return result
        },
        vehicles: async (opts) => {
          const { orderBy, ...filter } = opts
          const res = await this.$apollo.query({
            query: GET_VEHICLE_FILTER,
            variables: {
              filter: {
                status: ['I', 'R', 'A'],
                parentOnly: true,
                orderBy: orderBy ?? JSON.stringify({ licensePlate: 'asc' }),
                ...filter,
              },
            },
          })

          return res.data.vehiclesFilter.vehiclesFilterPaginated
        },
      }
    },
    templateId() {
      return this.$route.params.templateId
    },
    duplicateFromId() {
      return this.isEdit ? null : this.$route.query.duplicateFrom
    },
    isEdit() {
      return this.$route.params.templateId !== undefined
    },
    isDuplicate() {
      return !!this.duplicateFromId
    },
    title() {
      return this.isEdit
        ? this.$t('vehicleInspection.form.editTitle')
        : this.$t('vehicleInspection.form.createTitle')
    },
    actionItems() {
      let actionItems = []
      if (this.hasAccess('create')) {
        actionItems.push({
          label: this.$t('global.duplicate'),
          handler: () =>
            this.$router.push({
              name: 'Create Setting Template Vehicle Inspection',
              query: { duplicateFrom: this.templateId },
            }),
        })
      }
      if (this.hasAccess('delete')) {
        actionItems.push({
          label: this.$t('global.action.delete'),
          class: 'text-red',
          handler: () => {
            this.$refs['modal-confirm-delete'].show()
          },
        })
      }
      return actionItems
    },
    hasInvalidSections() {
      return hasInvalidInspectionSections(this.form.sections)
    },
    isSaveDisabled() {
      return this.isLoading || this.isSaving || this.hasInvalidSections
    },
  },
  watch: {
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.resetForm()
        const sourceId = this.templateId || this.duplicateFromId
        if (sourceId) this.getDetailTemplate(sourceId)
      },
    },
  },
  methods: {
    hasAccess(permission) {
      return hasAccess(this.$store, permission)
    },
    handleFormSubmit() {
      this.$refs.formRef.formSubmitted()
    },
    async handleSave() {
      if (hasInvalidInspectionSections(this.form.sections)) {
        this.$mcToast.error(
          `${this.$t('global.alert.text.failedSaved')}. ${this.$t(
            'vehicleInspection.toolTip.duplicateWarning'
          )}`
        )
        return
      }

      const mode = this.vehicleSelectionMode

      const payload = {
        name: this.form.name,
        description: this.form.description,
        sections: buildSectionsPayload(this.form.sections),
      }

      if (mode === VEHICLE_SELECTION_MODES.allVehicles) {
        payload.allVehicles = true
      } else if (mode === VEHICLE_SELECTION_MODES.selectedVehicles) {
        payload.vehicleIds = this.form.vehicleIds
      } else if (mode === VEHICLE_SELECTION_MODES.vehicleGroup) {
        payload.vgroupIds = this.form.vgroupIds
      }

      if (this.isEdit) {
        payload.templateId = Number(this.templateId)
      }

      this.isSaving = true
      try {
        const res = await this.$apollo.mutate({
          mutation: this.isEdit
            ? UPDATE_VEHICLE_INSPECTION_TEMPLATE
            : CREATE_VEHICLE_INSPECTION_TEMPLATE,
          variables: { input: payload },
        })
        const result = this.isEdit
          ? res.data.updateVehicleInspectionTemplate
          : res.data.createVehicleInspectionTemplate

        if (!result.isSuccessful) {
          this.$mcToast.error(result.message || this.$t('global.somethingWentWrong'))
          return false
        }

        this.$mcToast.success(
          this.$t(this.isEdit ? 'global.alert.text.changed' : 'global.alert.text.saved')
        )
        this.$router.push({ name: 'Setting Template Vehicle Inspection' })
      } catch (error) {
        this.$mcToast.error(error.message || this.$t('global.somethingWentWrong'))
      } finally {
        this.isSaving = false
      }
    },

    async getDetailTemplate(templateId) {
      this.isLoading = true
      try {
        const res = await this.$apollo.query({
          query: GET_VEHICLE_INSPECTION_TEMPLATE,
          fetchPolicy: 'no-cache',
          variables: {
            filter: { templateId: Number(templateId) },
          },
        })
        const result = res.data.vehicleInspectionTemplate

        if (!result.isSuccessful) {
          this.$mcToast.error(result.message || this.$t('global.somethingWentWrong'))
          return
        }

        const detail = result.vehicleInspectionTemplate
        const vehicles = detail.vehicles || []
        const vgroups = detail.vgroups || []

        let mode = VEHICLE_SELECTION_MODES.allVehicles
        if (!detail.allVehicles) {
          const totalVehicles = Number(detail.totalVehicles)
          mode =
            vehicles.length > 0 || (Number.isFinite(totalVehicles) && totalVehicles > 0)
              ? VEHICLE_SELECTION_MODES.selectedVehicles
              : VEHICLE_SELECTION_MODES.vehicleGroup
        }
        this.vehicleSelectionMode = mode

        const isVehicles = mode === VEHICLE_SELECTION_MODES.selectedVehicles
        const isGroups = mode === VEHICLE_SELECTION_MODES.vehicleGroup

        this.hasInaccessibleTarget = detail.hasInaccessibleTarget

        this.form = {
          name: this.isDuplicate ? getDuplicateName(detail.name) : detail.name,
          description: detail.description ?? '',
          vehicleIds: isVehicles ? vehicles.map((v) => v.id) : [],
          vgroupIds: isGroups ? vgroups.map((v) => v.id) : [],
          sections: mapSectionsToForm(detail.sections, this.isDuplicate),
        }
        this.fullObject.licensePlate = isVehicles ? vehicles : []
        this.fullObject.vgroup = isGroups ? vgroups : []
      } catch (error) {
        console.error(error)
        this.$mcToast.error(error.message || this.$t('global.somethingWentWrong'))
      } finally {
        this.isLoading = false
      }
    },

    resetForm() {
      this.form = {
        description: '',
        name: '',
        vgroupIds: [],
        vehicleIds: [],
        sections: [],
      }
      this.fullObject = { vgroup: [], licensePlate: [] }
      this.vehicleSelectionMode = VEHICLE_SELECTION_MODES.allVehicles
    },
    async handleDelete() {
      this.isDeleting = true
      try {
        const res = await this.$apollo.mutate({
          mutation: DELETE_VEHICLE_INSPECTION_TEMPLATE,
          variables: {
            input: {
              templateIds: [Number(this.templateId)],
            },
          },
        })
        const result = res.data.deleteVehicleInspectionTemplates

        if (!result.isSuccessful) {
          this.$mcToast.error(result.message || this.$t('global.somethingWentWrong'))
          return
        }

        this.$refs['modal-confirm-delete'].hide()
        this.$mcToast.success(this.$t('global.alert.text.delete'))
        this.$router.push({ name: 'Setting Template Vehicle Inspection' })
      } catch (error) {
        this.$mcToast.error(error.message || this.$t('global.somethingWentWrong'))
      } finally {
        this.isDeleting = false
      }
    },
  },
}
</script>
